import type { Node } from '@xyflow/react';

/**
 * Drives copy and controls for "Test in Chat" in the agent builder.
 * Inferred from workflow nodes so GitHub-focused agents don't show SQL-only UI.
 */
export interface AgentTestChatProfile {
    mode: 'sql' | 'github' | 'generic';
    emptyStateMessage: string;
    inputLabel: string;
    showDatabaseTerminalSelectors: boolean;
    promptCategories?: PromptCategorySection[];
}

export interface PromptCategorySection {
    title: string;
    description: string;
    prompts: string[];
}

const SQLISH_TOOL_IDS = new Set([
    'queryBuilder',
    'executeQuery',
    'executeRawSql',
    'searchQueries',
    'listQueries',
    'listTerminals',
    'getTerminalConfigs',
    'getRegions',
]);

const GITHUB_PROMPT_LIBRARY: PromptCategorySection[] = [
    {
        title: 'Read & explore',
        description: 'Fetch content from the configured repository',
        prompts: [
            'Read README.md and summarize the project in three bullet points.',
            'List files and folders at the root of the repository.',
            'Show the contents of package.json (or pom.xml / build.gradle if present).',
            'What is in src/main/java? List key packages or entry classes.',
        ],
    },
    {
        title: 'Branches & structure',
        description: 'Navigate the repo layout',
        prompts: [
            'What branches exist? Which one is the default base branch?',
            'Find documentation files (*.md) under docs/ or the repo root.',
            'Give a one-paragraph overview of the repository layout.',
        ],
    },
];

function collectEnabledToolIds(nodes: Node[]): Set<string> {
    const ids = new Set<string>();
    for (const n of nodes) {
        const d: Record<string, unknown> = (n.data || {}) as Record<string, unknown>;
        if (n.type === 'aiAction' && Array.isArray(d.enabledTools)) {
            (d.enabledTools as string[]).forEach((x) => ids.add(x));
        }
        if (n.type === 'tool') {
            if (typeof d.toolId === 'string' && d.toolId) ids.add(d.toolId);
            if (Array.isArray(d.enabledTools)) {
                (d.enabledTools as string[]).forEach((x) => ids.add(x));
            }
        }
    }
    return ids;
}

function hasSqlishTools(ids: Set<string>): boolean {
    return Array.from(ids).some((id) => SQLISH_TOOL_IDS.has(id));
}

export function inferAgentTestChatProfile(nodes: Node[], agentName: string): AgentTestChatProfile {
    const ids = collectEnabledToolIds(nodes);
    const hasGithub = ids.has('github');
    const hasSql = hasSqlishTools(ids);
    const label = agentName?.trim() || 'this agent';

    if (hasGithub && hasSql) {
        return {
            mode: 'generic',
            emptyStateMessage: `I'm testing "${label}". Ask about the configured GitHub repository or use database tools—I will use this agent's tools.`,
            inputLabel: 'What would you like to ask?',
            showDatabaseTerminalSelectors: true,
        };
    }

    if (hasGithub) {
        return {
            mode: 'github',
            emptyStateMessage: `I'm your GitHub assistant for "${label}". Ask about files, README, branches, or other questions for the repository you configured in this agent.`,
            inputLabel: 'What would you like to know about the repository?',
            showDatabaseTerminalSelectors: false,
            promptCategories: GITHUB_PROMPT_LIBRARY,
        };
    }

    if (!hasSql && ids.size > 0) {
        return {
            mode: 'generic',
            emptyStateMessage: `I'm testing "${label}". Ask a question—I will use the tools enabled for this agent.`,
            inputLabel: 'What would you like to ask?',
            showDatabaseTerminalSelectors: false,
        };
    }

    // No tools assigned at all — pure conversational agent, no SQL UI
    if (ids.size === 0) {
        return {
            mode: 'generic',
            emptyStateMessage: `I'm testing "${label}". No tools are assigned—this agent will respond using only its prompt context.`,
            inputLabel: 'What would you like to ask?',
            showDatabaseTerminalSelectors: false,
        };
    }

    return {
        mode: 'sql',
        emptyStateMessage: "I'm your SQL Assistant. Ask me to help you build or explain your query.",
        inputLabel: 'What query do you want to build?',
        showDatabaseTerminalSelectors: true,
    };
}
